import React, { useState, useCallback, useContext, useRef, memo } from 'react';
import { useRemountOnPageHidden } from '../../../hooks/useRemountOnPageHidden';
import {
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  useIonRouter,
  IonPage,
  IonGrid,
  IonRow,
  IonHeader,
  IonToolbar,
  IonAvatar,
  IonButtons,
  IonButton,
  IonItem,
  IonLabel,
  IonText,
  IonList,
  IonReorder,
  IonReorderGroup,
  IonCheckbox,
  ItemReorderEventDetail,
  IonModal,
  IonTitle,
  IonSpinner
} from '@ionic/react';
import {
  cashOutline,
  notifications,
  documentTextOutline,
  walletOutline,
  chatbubblesOutline,
  pencil,
  closeCircle,
} from 'ionicons/icons';
import { useAuth } from '../../../contexts';
import { AuthContext } from '../../../contexts/auth-context-core';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { Shortcut } from '@rsmaids/shared/types/dashboard';
import { FINANCE_SHORTCUTS, FINANCE_CATEGORY_NAMES } from '../shortcutCatalogues';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import styles from '../Dashboard.module.scss';
import { useTranslation } from 'react-i18next';

const DashboardFinance: React.FC = () => {
  const { t } = useTranslation();
  // The scrolling area is rebuilt while the page is hidden, so it is back at the top on return
  const scrollAreaRef = useRef<HTMLIonContentElement>(null);
  const scrollResetKey = useRemountOnPageHidden(scrollAreaRef);
  const router = useIonRouter();
  const { authState } = useAuth();
  const { user } = authState;
  const authContext = useContext(AuthContext);

  const goToAlerts = () => {
    router.push('/tabs/dashboard/finance/alerts');
  };

  const [isEditMode, setIsEditMode] = useState(false);

  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(
    user?.preferences?.dashboardShortcuts || ['invoices', 'reports', 'profile', 'alerts', 'settings']
  );

  const [originalShortcutIds, setOriginalShortcutIds] = useState<string[]>([]);
  const [isSavingShortcuts, setIsSavingShortcuts] = useState(false);

  // Update shortcuts when user preferences change — computed during render
  // (React's "adjust state when a prop changes" pattern) rather than as a
  // setState call inside an effect.
  const [prevDashboardShortcuts, setPrevDashboardShortcuts] = useState(user?.preferences?.dashboardShortcuts);
  if (prevDashboardShortcuts !== user?.preferences?.dashboardShortcuts) {
    setPrevDashboardShortcuts(user?.preferences?.dashboardShortcuts);
    if (user?.preferences?.dashboardShortcuts) {
      setActiveShortcutIds(user.preferences.dashboardShortcuts);
    }
  }

  // When opening edit mode, save the current state
  const toggleEditMode = () => {
    if (!isEditMode) {
      setOriginalShortcutIds([...activeShortcutIds]); // Save current state
    }
    setIsEditMode(!isEditMode);
  };

  const saveShortcutsToStorage = useCallback(async (shortcuts: string[]) => {
    try {
      // Save to server for cross-device sync
      await DataServiceFactory.getService().auth.updateUserPreferences({
        dashboardShortcuts: shortcuts
      });

      authContext?.actions?.updateLocalPreferences({ dashboardShortcuts: shortcuts });
      localStorage.setItem('dashboard-finance-shortcuts', JSON.stringify(shortcuts));
    } catch (error) {
      console.error('Failed to save shortcuts to server:', error);
      // Fallback to localStorage if server save fails
      localStorage.setItem('dashboard-finance-shortcuts', JSON.stringify(shortcuts));
    }
  }, [authContext]);

  // Computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect,
  // since this is a pure derivation from user/localStorage with no
  // external system being subscribed to.
  const [prevUserForShortcuts, setPrevUserForShortcuts] = useState(user);
  if (prevUserForShortcuts !== user) {
    setPrevUserForShortcuts(user);
    if (user) {
      // Check if user has saved preferences first
      if (user.preferences?.dashboardShortcuts && user.preferences.dashboardShortcuts.length > 0) {
        setActiveShortcutIds(user.preferences.dashboardShortcuts);
      } else {
        // Check localStorage as secondary fallback
        const localShortcuts = localStorage.getItem('dashboard-finance-shortcuts');
        if (localShortcuts) {
          try {
            const parsedShortcuts = JSON.parse(localShortcuts);
            if (Array.isArray(parsedShortcuts) && parsedShortcuts.length > 0) {
              setActiveShortcutIds(parsedShortcuts);
            } else {
              // Only set defaults if no preferences exist anywhere
              setActiveShortcutIds(['invoices', 'reports', 'profile', 'alerts', 'settings']);
            }
          } catch {
            // Only set defaults if localStorage is corrupted
            setActiveShortcutIds(['invoices', 'reports', 'profile', 'alerts', 'settings']);
          }
        } else {
          // Only set defaults if no localStorage exists
          setActiveShortcutIds(['invoices', 'reports', 'profile', 'alerts', 'settings']);
        }
      }
    }
  }

  const handleSaveShortcuts = async () => {
    setIsSavingShortcuts(true);
    try {
      await saveShortcutsToStorage(activeShortcutIds);
    } finally {
      setIsSavingShortcuts(false);
    }
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    // Restore original state
    setActiveShortcutIds(originalShortcutIds);
    setIsEditMode(false);
  };

  const allShortcuts: Shortcut[] = FINANCE_SHORTCUTS;
  const categoryNames = FINANCE_CATEGORY_NAMES;

  // Computed values
  const activeShortcuts = activeShortcutIds
    .map(id => allShortcuts.find(s => s.id === id))
    .filter((shortcut): shortcut is Shortcut => shortcut !== undefined);

  const availableShortcuts = allShortcuts.filter(shortcut =>
    !activeShortcutIds.includes(shortcut.id)
  );

  const handleReorder = async (event: CustomEvent<ItemReorderEventDetail>) => {
    event.detail.complete();
    const newOrder = [...activeShortcutIds];
    const movedItem = newOrder.splice(event.detail.from, 1)[0];
    newOrder.splice(event.detail.to, 0, movedItem);
    setActiveShortcutIds(newOrder);
    // Save immediately on reorder
    saveShortcutsToStorage(newOrder);
  };

  const handleToggleShortcut = async (shortcutId: string, isActive: boolean) => {
    const newShortcuts = isActive
      ? activeShortcutIds.filter(id => id !== shortcutId)
      : [...activeShortcutIds, shortcutId];
    setActiveShortcutIds(newShortcuts);
    // Save immediately on toggle
    await saveShortcutsToStorage(newShortcuts);
  };

  const groupedAvailableShortcuts = Object.entries(categoryNames).map(([categoryId, categoryName]) => ({
    id: categoryId,
    name: categoryName,
    shortcuts: availableShortcuts.filter(s => s.category === categoryId)
  })).filter(category => category.shortcuts.length > 0);

  return (
    <IonPage>
      <IonHeader className={styles.dashboardHeader}>
        <IonToolbar>
          <IonButton
            slot="start"
            fill="clear"
            className={styles.avatarButton}
            onClick={() => router.push('/profile')}
            title={t('pages.dashboard.finance.DashboardFinance.viewProfile')}
            aria-label={t('pages.dashboard.finance.DashboardFinance.viewProfile')}
          >
            <IonAvatar slot="icon-only" className={styles.userAvatar}>
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user?.name || t('pages.dashboard.finance.DashboardFinance.user')} />
              ) : (
                <IonText className={styles.defaultAvatar}>
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </IonText>
              )}
            </IonAvatar>
          </IonButton>
          <IonLabel>
            <h3 className={styles.welcomeText}>{t('pages.dashboard.finance.DashboardFinance.welcomeBack')}</h3>
            <h2 className={styles.userName}>{user?.name || t('pages.dashboard.finance.DashboardFinance.financeManager')}</h2>
          </IonLabel>
          <IonButtons slot="end">
            <IonButton
              className={styles.headerIconButton}
              onClick={() => router.push('/dashboard/team-messaging')}
              title={t('pages.dashboard.finance.DashboardFinance.teamMessaging')}
            >
              <IonIcon icon={chatbubblesOutline} slot="icon-only" />
            </IonButton>
            <IonButton
              className={styles.headerIconButton}
              onClick={goToAlerts}
              title={t('pages.dashboard.finance.DashboardFinance.alerts')}
            >
              <IonIcon icon={notifications} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      
      <IonContent>
        <IonGrid className={styles.dashboardContainer}>
          {/* Menu Cards - native horizontal scroll (see .scrollContainer) */}
          <IonRow className={styles.scrollContainer}>
            <IonButton
              fill="clear"
              className={`${styles.menuCard} ${styles.editButton}`}
              onClick={toggleEditMode}
            >
              <IonLabel className={styles.menuCardContent}>
                <IonIcon icon={pencil} className={styles.menuIcon} />
              </IonLabel>
            </IonButton>

            {activeShortcuts.map((shortcut) => (
              <IonButton
                key={shortcut.id}
                fill="clear"
                className={styles.menuCard}
                onClick={() => router.push(shortcut.route)}
              >
                <IonLabel className={styles.menuCardContent}>
                  <IonIcon icon={shortcut.icon} className={styles.menuIcon} />
                  <IonText className={styles.menuTitle}>{shortcut.title}</IonText>
                </IonLabel>
              </IonButton>
            ))}
          </IonRow>

          {/* All content below menu becomes scrollable */}
          <IonContent key={scrollResetKey} ref={scrollAreaRef} className={styles.scrollableDashboard}>
            {/* Financial Overview Card */}
            <IonCard className={styles.welcomeCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.welcomeTitle}>{t('pages.dashboard.finance.DashboardFinance.financialOverview')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonRow className={styles.scrollContainer}>
                  <IonCard className={styles.statsCard}>
                    <IonCardContent className={styles.statsCardContentCentered}>
                      <IonIcon icon={cashOutline} color="success" className={styles.statsCardIcon} />
                      <IonText className={styles.statsCardLabel}>{t('pages.dashboard.finance.DashboardFinance.revenue')}</IonText>
                      <IonText className={styles.statsCardValue}>$12,450</IonText>
                      <IonText className={styles.statsCardPeriod}>{t('pages.dashboard.finance.DashboardFinance.thisMonth')}</IonText>
                    </IonCardContent>
                  </IonCard>

                  <IonCard className={styles.statsCard}>
                    <IonCardContent className={styles.statsCardContentCentered}>
                      <IonIcon icon={walletOutline} color="danger" className={styles.statsCardIcon} />
                      <IonText className={styles.statsCardLabel}>{t('pages.dashboard.finance.DashboardFinance.expenses')}</IonText>
                      <IonText className={styles.statsCardValue}>$8,320</IonText>
                      <IonText className={styles.statsCardPeriod}>{t('pages.dashboard.finance.DashboardFinance.thisMonth')}</IonText>
                    </IonCardContent>
                  </IonCard>

                  <IonCard className={styles.statsCard}>
                    <IonCardContent className={styles.statsCardContentCentered}>
                      <IonIcon icon={documentTextOutline} color="tertiary" className={styles.statsCardIcon} />
                      <IonText className={styles.statsCardLabel}>{t('pages.dashboard.finance.DashboardFinance.invoices')}</IonText>
                      <IonText className={styles.statsCardValue}>24</IonText>
                      <IonText className={styles.statsCardPeriod}>{t('pages.dashboard.finance.DashboardFinance.pending')}</IonText>
                    </IonCardContent>
                  </IonCard>
                </IonRow>
              </IonCardContent>
            </IonCard>
            
            {/* Recent Invoices Card */}
            <IonCard className={styles.welcomeCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.welcomeTitle}>{t('pages.dashboard.finance.DashboardFinance.recentInvoices')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className={styles.welcomeContent}>
                <IonList lines="none">
                  <IonItem>
                    <IonLabel>{t('pages.dashboard.finance.DashboardFinance.noRecentInvoicesToDisplay')}</IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
            
            {/* Overdue Payments Card */}
            <IonCard className={styles.welcomeCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.welcomeTitle}>{t('pages.dashboard.finance.DashboardFinance.overduePayments')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className={styles.welcomeContent}>
                <IonList lines="none">
                  <IonItem>
                    <IonLabel>{t('pages.dashboard.finance.DashboardFinance.noOverduePaymentsToDisplay')}</IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Modal (stays outside the scrollable area) */}
            <IonModal
              isOpen={isEditMode}
              onDidDismiss={() => setIsEditMode(false)}
              className={styles.editModal}
              aria-labelledby="edit-shortcuts-title"
              aria-describedby="edit-shortcuts-description"
            >
              <IonHeader className='ion-no-border'>
                <IonToolbar >
                  <IonButtons slot="start">
                    <IonButton
                      fill="clear"
                      color="danger"
                      onClick={handleCancelEdit}
                      className={styles.cancelButton}
                    >
                      <IonIcon icon={closeCircle} />
                    </IonButton>
                    <IonLabel className={styles.titleStack}>
                      <IonTitle id="edit-shortcuts-title">{t('pages.dashboard.finance.DashboardFinance.editShortcuts')}</IonTitle>
                    </IonLabel>
                  </IonButtons>
                  <IonButtons slot="end">
                    <IonButton
                      fill="clear"
                      color="primary"
                      shape='round'
                      onClick={handleSaveShortcuts}
                      className={styles.saveButton}
                      disabled={isSavingShortcuts}
                    >
                      {isSavingShortcuts ? <IonSpinner name="dots" /> : t('pages.dashboard.finance.DashboardFinance.save')}
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>

              <IonContent>
                <IonList>
                  {/* Active shortcuts with reorder */}
                  <IonItem lines="none" className={styles.sectionHeader}>
                    <IonLabel>
                      <h2>{t('pages.dashboard.finance.DashboardFinance.activeShortcuts')}</h2>
                      <p id="edit-shortcuts-description">{t('pages.dashboard.finance.DashboardFinance.dragToReorderUncheckToRemove')}</p>
                    </IonLabel>
                  </IonItem>

                  <IonReorderGroup disabled={!isEditMode} onIonReorderEnd={handleReorder}>
                    {activeShortcuts.map((shortcut) => (
                      <IonItem key={shortcut.id} className={styles.shortcutItem}>
                        <IonCheckbox
                          slot="start"
                          checked={true}
                          onIonChange={() => handleToggleShortcut(shortcut.id, true)}
                        />
                        <IonIcon icon={shortcut.icon} className={styles.shortcutIcon} />
                        <IonLabel>
                          <h3>{shortcut.title}</h3>
                          <p>{categoryNames[shortcut.category]}</p>
                        </IonLabel>
                        <IonReorder slot="end" />
                      </IonItem>
                    ))}
                  </IonReorderGroup>

                  {/* Available shortcuts by category */}
                  {groupedAvailableShortcuts.map((category) => (
                    <div key={category.id}>
                      <IonItem lines="none" className={styles.sectionHeader}>
                        <IonLabel>
                          <h2>{category.name}</h2>
                          <p>{t('pages.dashboard.finance.DashboardFinance.checkToAddToDashboard')}</p>
                        </IonLabel>
                      </IonItem>

                      {category.shortcuts.map((shortcut) => (
                        <IonItem key={shortcut.id} className={styles.shortcutItem}>
                          <IonCheckbox
                            slot="start"
                            checked={false}
                            onIonChange={() => handleToggleShortcut(shortcut.id, false)}
                          />
                          <IonIcon icon={shortcut.icon} className={styles.shortcutIcon} />
                          <IonLabel>
                            <h3>{shortcut.title}</h3>
                            <p>{category.name}</p>
                          </IonLabel>
                        </IonItem>
                      ))}
                    </div>
                  ))}
                </IonList>
              </IonContent>
            </IonModal>
          </IonContent>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default memo(DashboardFinance);